import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type AddPremiumInput,
  type AddValueSnapshotInput,
  type CreateInsuranceInput,
  type Insurance,
  type InsuranceDetail,
  type InsuranceSummary,
  type ListInsurancesFilters,
  type UpdateInsuranceInput,
  insuranceApi,
} from "../lib/insurance";

const baseKey = ["insurance"] as const;

export function useInsurances(filters: ListInsurancesFilters = {}) {
  return useQuery<{ insurances: Insurance[] }>({
    queryKey: [...baseKey, "list", filters],
    queryFn: () => insuranceApi.list(filters),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useInsuranceSummary() {
  return useQuery<InsuranceSummary>({
    queryKey: [...baseKey, "summary"],
    queryFn: () => insuranceApi.summary(),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useInsurance(id: string) {
  return useQuery<InsuranceDetail>({
    queryKey: [...baseKey, "detail", id],
    queryFn: () => insuranceApi.get(id),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(id),
  });
}

export function useCreateInsurance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInsuranceInput) => insuranceApi.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: baseKey });
      qc.invalidateQueries({ queryKey: ["cashflow"] });
    },
  });
}

export function useUpdateInsurance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateInsuranceInput }) =>
      insuranceApi.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: baseKey });
      qc.invalidateQueries({ queryKey: ["cashflow"] });
    },
  });
}

export function useSoftDeleteInsurance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => insuranceApi.softDelete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: baseKey });
      qc.invalidateQueries({ queryKey: ["cashflow"] });
    },
  });
}

export function useHardDeleteInsurance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => insuranceApi.hardDelete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: baseKey });
      qc.invalidateQueries({ queryKey: ["cashflow"] });
    },
  });
}

export function useAddPremium() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: AddPremiumInput }) =>
      insuranceApi.addPremium(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: baseKey });
      qc.invalidateQueries({ queryKey: ["cashflow"] });
    },
  });
}

export function useDeletePremium() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, premiumId }: { id: string; premiumId: string }) =>
      insuranceApi.deletePremium(id, premiumId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: baseKey });
      qc.invalidateQueries({ queryKey: ["cashflow"] });
    },
  });
}

export function useAddValueSnapshot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: AddValueSnapshotInput }) =>
      insuranceApi.addValueSnapshot(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: baseKey });
      qc.invalidateQueries({ queryKey: ["allocation"] });
      qc.invalidateQueries({ queryKey: ["networth"] });
    },
  });
}

export function useDeleteValueSnapshot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, snapshotId }: { id: string; snapshotId: string }) =>
      insuranceApi.deleteValueSnapshot(id, snapshotId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: baseKey });
      qc.invalidateQueries({ queryKey: ["allocation"] });
      qc.invalidateQueries({ queryKey: ["networth"] });
    },
  });
}
